/**
 * Förderantrag Context - Vorbereitet für digitale Förderanträge
 * 
 * Zukünftige Features:
 * - Strukturierte Erfassung der Antragsdaten
 * - Dokumenten-Upload
 * - Antragsstatus-Tracking
 * - Interne Bearbeitung durch asK-mi
 * - Datenschutz und Zugriffsschutz
 */

import React, { createContext, useContext, useState } from 'react';

// Antragsstatus
export const APPLICATION_STATUS = {
  DRAFT: 'entwurf',
  SUBMITTED: 'eingereicht',
  IN_REVIEW: 'in_pruefung',
  ADDITIONAL_INFO: 'rueckfrage',
  APPROVED: 'genehmigt',
  REJECTED: 'abgelehnt',
  COMPLETED: 'abgeschlossen',
};

// Antragstypen
export const APPLICATION_TYPES = {
  SELBSTHILFE: 'selbsthilfe',
  EINRICHTUNG: 'einrichtung',
  PROJEKT: 'projekt',
  FORSCHUNG: 'forschung',
  SONSTIGES: 'sonstiges',
};

// Antragsstruktur
export const createEmptyApplication = () => ({
  id: null,
  type: null,
  status: APPLICATION_STATUS.DRAFT,
  createdAt: null,
  updatedAt: null,
  
  // Antragsteller
  applicant: {
    type: '', // person, organisation, initiative
    name: '',
    organisation: '',
    email: '',
    phone: '',
    address: {
      street: '',
      zip: '',
      city: '',
    },
  },
  
  // Vorhaben
  project: {
    title: '',
    description: '',
    targetGroup: '',
    goals: '',
    timeline: '',
    location: '',
  },
  
  // Unterstützungsbedarf
  support: {
    type: [], // financial, rooms, material, networking, development
    financialAmount: null,
    financialPurpose: '',
    otherSupport: '',
  },
  
  // Dokumente
  documents: [],
  
  // Interne Felder (für asK-mi)
  internal: {
    assignedTo: null,
    notes: [],
    decision: null,
    decisionDate: null,
  },
});

const FoerderantragContext = createContext(null);

export function FoerderantragProvider({ children }) {
  const [applications, setApplications] = useState([]);
  const [currentApplication, setCurrentApplication] = useState(null);

  // Zukünftige Funktionen
  const createApplication = (type) => {
    const newApp = {
      ...createEmptyApplication(),
      id: `draft-${Date.now()}`,
      type,
      createdAt: new Date().toISOString(),
    };
    setCurrentApplication(newApp);
    return newApp;
  };

  const updateApplication = (updates) => {
    if (!currentApplication) return;
    setCurrentApplication(prev => ({
      ...prev,
      ...updates,
      updatedAt: new Date().toISOString(),
    }));
  };

  const submitApplication = async () => {
    // TODO: Implementierung mit Backend
    console.log('Antrag-Einreichung vorbereitet');
    return { 
      success: false, 
      message: 'Digitale Förderanträge werden in einer zukünftigen Version aktiviert. Bitte kontaktieren Sie uns per E-Mail.' 
    };
  };

  const uploadDocument = async (file) => {
    // TODO: Implementierung mit Backend
    console.log('Dokument-Upload vorbereitet für:', file?.name);
    return { success: false, message: 'Dokument-Upload wird in einer zukünftigen Version aktiviert.' };
  };

  const value = {
    applications,
    currentApplication,
    createApplication,
    updateApplication,
    submitApplication,
    uploadDocument,
    APPLICATION_STATUS,
    APPLICATION_TYPES,
  };

  return (
    <FoerderantragContext.Provider value={value}>
      {children}
    </FoerderantragContext.Provider>
  );
}

export function useFoerderantrag() {
  const context = useContext(FoerderantragContext);
  if (!context) {
    throw new Error('useFoerderantrag must be used within a FoerderantragProvider');
  }
  return context;
}

export default FoerderantragContext;
